import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Check, ShoppingCart, Zap, MessageCircle, ArrowRight, Sparkles, Info } from 'lucide-react';
import PageHero from '../components/PageHero';
import { useCart } from '../context/CartContext';
import CheckoutDialog from '../components/CheckoutDialog';
import { brand } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatINR(n) {
  return `₹${Number(n).toLocaleString('en-IN')}`;
}

function planDisplayAmount(plan) {
  if (plan.custom_quote) return 'Custom';
  if (plan.gst_included) return formatINR(plan.base_amount);
  return `${formatINR(plan.base_amount)} + GST`;
}

export default function PricingPage() {
  const [plans, setPlans] = useState([]);
  const [checkoutPlan, setCheckoutPlan] = useState(null);
  const { add, count, setDrawerOpen } = useCart();

  useEffect(() => {
    axios.get(`${API}/plans`).then((r) => setPlans(r.data.plans || [])).catch(() => {});
  }, []);

  const handleBuyNow = (plan) => {
    if (plan.custom_quote) {
      window.open(`https://wa.me/${brand.whatsapp}?text=${encodeURIComponent('Hi EC Safetrade, I would like a custom quote for Website Development.')}`, '_blank');
      return;
    }
    setCheckoutPlan(plan);
  };

  const handleAddToCart = (plan) => {
    if (plan.custom_quote) {
      window.open(`https://wa.me/${brand.whatsapp}?text=${encodeURIComponent('Hi EC Safetrade, I would like a custom quote for Website Development.')}`, '_blank');
      return;
    }
    add(plan);
  };

  return (
    <>
      <PageHero
        eyebrow="Plans & Pricing"
        title="Simple, Transparent Pricing"
        sub="Choose the right plan for your brand. All plans include a dedicated account manager and priority WhatsApp support."
        accent="orange"
      />

      <section className="section bg-white">
        <div className="container-x">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
            <div className="inline-flex items-center gap-2 bg-[color:var(--brand-green-light)] text-[color:var(--brand-green-dark)] px-4 py-2 rounded-full text-xs font-semibold">
              <Sparkles size={14} /> Have a coupon? Apply it at checkout for extra savings.
            </div>
            <button
              onClick={() => setDrawerOpen(true)}
              className="inline-flex items-center gap-2 bg-[color:var(--brand-green)] text-white font-semibold px-5 py-2.5 rounded-full text-sm hover:bg-[color:var(--brand-green-dark)] transition shadow-md"
            >
              <ShoppingCart size={16} /> View Cart ({count})
            </button>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {plans.map((p) => (
              <div
                key={p.id}
                className={`relative bg-white rounded-3xl p-7 border-2 shadow-md hover:shadow-xl transition-all ${
                  p.highlight
                    ? 'border-[color:var(--brand-orange)]'
                    : 'border-neutral-100 hover:border-[color:var(--brand-green)]/30'
                }`}
              >
                {p.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[color:var(--brand-orange)] text-white text-[11px] font-bold uppercase tracking-wider px-4 py-1 rounded-full shadow-md">
                    Best value
                  </div>
                )}
                <div className="flex items-center gap-2 mb-3">
                  <span
                    className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest"
                    style={{
                      background: p.custom_quote ? '#FFF3D6' : 'var(--brand-green-light)',
                      color: p.custom_quote ? '#946200' : 'var(--brand-green-dark)',
                    }}
                  >
                    {p.duration}
                  </span>
                </div>
                <h3 className="font-black text-[color:var(--ink)] text-xl leading-tight mb-4 min-h-[56px]">
                  {p.name}
                </h3>

                <div className="mb-5">
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-black text-[color:var(--brand-green)]">
                      {p.custom_quote ? 'Custom' : formatINR(p.base_amount)}
                    </span>
                  </div>
                  <div className="text-xs text-[color:var(--ink-soft)] mt-1">
                    {p.custom_quote
                      ? 'Tailored quote based on scope'
                      : p.gst_included
                      ? 'GST included'
                      : '+ 18% GST'}
                  </div>
                </div>

                <ul className="space-y-2 mb-6">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-[color:var(--ink)]">
                      <Check size={16} className="text-[color:var(--brand-green)] mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <div className="space-y-2">
                  <button
                    onClick={() => handleBuyNow(p)}
                    className="w-full inline-flex items-center justify-center gap-2 bg-[color:var(--brand-green)] text-white font-semibold py-3 rounded-full text-sm hover:bg-[color:var(--brand-green-dark)] transition shadow-md"
                  >
                    {p.custom_quote ? (
                      <>
                        <MessageCircle size={16} /> Get Custom Quote
                      </>
                    ) : (
                      <>
                        <Zap size={16} /> Buy Now
                      </>
                    )}
                  </button>
                  {!p.custom_quote && (
                    <button
                      onClick={() => handleAddToCart(p)}
                      className="w-full inline-flex items-center justify-center gap-2 bg-white text-[color:var(--brand-green)] font-semibold py-3 rounded-full text-sm border-2 border-[color:var(--brand-green)] hover:bg-[color:var(--brand-green-light)] transition"
                    >
                      <ShoppingCart size={16} /> Add to Cart
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-4">
            {[
              { title: 'Secure Payments', desc: 'Powered by Razorpay — UPI, cards, netbanking, wallets.' },
              { title: 'Instant Onboarding', desc: 'Kickoff call within 24 hours of payment confirmation.' },
              { title: 'Coupon Codes', desc: 'Save more with our welcome offers and festive discounts.' },
            ].map((x) => (
              <div key={x.title} className="bg-[color:var(--brand-green-light)]/40 rounded-2xl p-5 border border-[color:var(--brand-green)]/15">
                <div className="flex items-center gap-2 font-bold text-[color:var(--ink)] mb-1">
                  <Info size={16} className="text-[color:var(--brand-green)]" /> {x.title}
                </div>
                <div className="text-sm text-[color:var(--ink-soft)]">{x.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CheckoutDialog
        plan={checkoutPlan}
        open={!!checkoutPlan}
        onOpenChange={(v) => !v && setCheckoutPlan(null)}
      />
    </>
  );
}
